import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { 
  getStoredData, 
  saveStoredData, 
  getSettings, 
  saveSettings, 
  getRecipients, 
  getLastRecipientId, 
  setLastRecipientId,
  getArticles,
  getLastArticleId,
  setLastArticleId
} from '../utils/storage';

const { FiPlus, FiTrash, FiSave, FiArrowLeft, FiChevronDown } = FiIcons;

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useMemo(() => getSettings(), []);
  const recipients = useMemo(() => getRecipients(), []);
  const articles = useMemo(() => getArticles(), []);

  const [formData, setFormData] = useState({
    id: '',
    number: '',
    date: new Date().toISOString().split('T')[0],
    recipientId: '',
    recipientName: '',
    recipientAddress: '',
    items: []
  });

  const [activeArticlePicker, setActiveArticlePicker] = useState(null);

  useEffect(() => {
    if (id) {
      const existing = getStoredData().find(item => item.id === id);
      if (existing) {
        // Preise beim Laden als String mit 2 Nachkommastellen formatieren für die Anzeige
        const formattedItems = existing.items.map(i => ({
          ...i,
          price: Number(i.price).toFixed(2),
          quantity: i.quantity // Menge lassen wir so, meist ganzzahlig
        }));
        setFormData({ ...existing, items: formattedItems });
      }
    } else {
      const lastRecId = getLastRecipientId();
      const lastRec = recipients.find(r => r.id === lastRecId);
      
      const lastArtId = getLastArticleId();
      const lastArt = articles.find(a => a.id === lastArtId) || articles[0];
      
      setFormData(prev => ({
        ...prev,
        number: `${settings.prefix}${settings.nextNumber}`,
        recipientId: lastRecId || '',
        recipientName: lastRec?.name || '',
        recipientAddress: lastRec?.address || '',
        items: [{ 
          id: Date.now(), 
          articleId: lastArt?.id || '',
          description: lastArt?.name || 'Stempelnutzung', 
          quantity: 1, 
          price: lastArt?.price ? Number(lastArt.price).toFixed(2) : '0.00'
        }]
      }));
    }
  }, [id, settings, recipients, articles]);

  const formatCurrency = (amount) => {
    return Number(amount).toFixed(2).replace('.', ',') + ' €';
  };

  const handleRecipientSelect = (recId) => {
    const rec = recipients.find(r => r.id === recId);
    if (rec) {
      setFormData(prev => ({
        ...prev,
        recipientId: rec.id,
        recipientName: rec.name,
        recipientAddress: rec.address
      }));
    } else {
      setFormData(prev => ({ ...prev, recipientId: '', recipientName: '', recipientAddress: '' }));
    }
  };

  const selectArticle = (itemId, article) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { 
          ...item, 
          articleId: article.id,
          description: article.name, 
          price: Number(article.price).toFixed(2) // Auch hier formatieren
        } : item
      )
    }));
    setActiveArticlePicker(null);
  };

  const updateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { 
          ...item, 
          // Bei Preis/Menge den Raw-String speichern, damit Eingabe (z.B. "20.") möglich ist
          [field]: field === 'description' ? value : value 
        } : item
      )
    }));
  };

  // Neu: Formatiert den Preis beim Verlassen des Feldes
  const handlePriceBlur = (itemId, value) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      updateItem(itemId, 'price', num.toFixed(2));
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (formData.recipientId) setLastRecipientId(formData.recipientId);
    
    if (formData.items[0]?.articleId) {
      setLastArticleId(formData.items[0].articleId);
    }
    
    // Vor dem Speichern sicherstellen, dass Zahlen als Numbers gespeichert werden
    const dataToSave = {
      ...formData,
      items: formData.items.map(item => ({
        ...item,
        quantity: Number(item.quantity),
        price: Number(item.price)
      }))
    };

    const existingData = getStoredData();
    if (id) {
      const newData = existingData.map(item => item.id === id ? dataToSave : item);
      saveStoredData(newData);
    } else {
      const newEntry = { ...dataToSave, id: Date.now().toString() };
      saveStoredData([...existingData, newEntry]);
      saveSettings({ ...settings, nextNumber: parseInt(settings.nextNumber) + 1 });
    }
    navigate('/');
  };

  // Berechnungen mit Number() Wrapper, da items.price jetzt Strings sein können
  const nettoTotal = formData.items.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.price)), 0);
  const vatAmount = nettoTotal * 0.19;
  const bruttoTotal = nettoTotal + vatAmount;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600">
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </button>
        <h2 className="text-2xl font-bold text-slate-900">{id ? 'Gutschrift bearbeiten' : 'Neue Gutschrift erstellen'}</h2>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Empfänger-Sektion */}
        <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200 shadow-sm space-y-6">
          <div className="grid md:grid-cols-2 gap-6 pb-6 border-b border-slate-100">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Gutschriftsnummer</label>
                <input required className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" value={formData.number} onChange={e => setFormData({ ...formData, number: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Datum</label>
                <input type="date" required className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center justify-between">
                  Empfänger auswählen
                  <button type="button" onClick={() => navigate('/recipients')} className="text-blue-600 text-xs hover:underline">Verwalten</button>
                </label>
                <select className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-white font-medium" value={formData.recipientId} onChange={e => handleRecipientSelect(e.target.value)}>
                  <option value="">-- Neu eingeben --</option>
                  {recipients.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Name / Firma</label>
                <input required className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" value={formData.recipientName} onChange={e => setFormData({ ...formData, recipientName: e.target.value, recipientId: '' })} />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Anschrift</label>
            <textarea required rows="2" className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none resize-none" value={formData.recipientAddress} onChange={e => setFormData({ ...formData, recipientAddress: e.target.value, recipientId: '' })} />
          </div>
        </div>

        {/* Positions-Sektion */}
        <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Positionen</h3>
            <button 
              type="button" 
              onClick={() => setFormData(f => ({ ...f, items: [...f.items, { id: Date.now(), description: '', quantity: 1, price: '0.00' }] }))} 
              className="text-blue-600 text-sm font-bold flex items-center gap-1 hover:text-blue-700"
            >
              <SafeIcon icon={FiPlus} /> Position hinzufügen
            </button>
          </div>

          <div className="space-y-3">
            {formData.items.map((item, index) => (
              <div key={item.id} className="flex gap-4 items-end relative">
                <div className="flex-[3] relative">
                  {index === 0 && <label className="block text-xs font-bold text-slate-500 mb-1">Beschreibung / Artikel</label>}
                  <div className="relative group">
                    <input 
                      className="w-full pl-3 pr-10 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" 
                      value={item.description} 
                      onChange={e => updateItem(item.id, 'description', e.target.value)} 
                    />
                    <button 
                      type="button"
                      onClick={() => setActiveArticlePicker(activeArticlePicker === item.id ? null : item.id)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-blue-600 border-l border-slate-100 pl-2"
                    >
                      <SafeIcon icon={FiChevronDown} />
                    </button>

                    {activeArticlePicker === item.id && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white ring-1 ring-black/5 shadow-xl rounded-xl z-50 max-h-48 overflow-y-auto">
                        <div className="p-2 grid gap-1">
                          <p className="text-[10px] uppercase font-bold text-slate-400 px-2 py-1">Gespeicherte Artikel</p>
                          {articles.map(art => (
                            <button
                              key={art.id}
                              type="button"
                              onClick={() => selectArticle(item.id, art)}
                              className="text-left px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors flex justify-between items-center group"
                            >
                              <span className="font-medium text-slate-700">{art.name}</span>
                              <span className="text-xs text-blue-600 font-bold opacity-0 group-hover:opacity-100">{formatCurrency(art.price)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-20">
                  {index === 0 && <label className="block text-xs font-bold text-slate-500 mb-1">Menge</label>}
                  <input 
                    type="number" 
                    className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm" 
                    value={item.quantity} 
                    onChange={e => updateItem(item.id, 'quantity', e.target.value)} 
                  />
                </div>
                <div className="w-28">
                  {index === 0 && <label className="block text-xs font-bold text-slate-500 mb-1">Einzel (€)</label>}
                  <input 
                    type="number" 
                    step="0.01" 
                    className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm" 
                    value={item.price} 
                    onChange={e => updateItem(item.id, 'price', e.target.value)}
                    onBlur={e => handlePriceBlur(item.id, e.target.value)}
                  />
                </div>
                
                {/* Zeilensumme */}
                <div className="w-32">
                  {index === 0 && <label className="block text-xs font-bold text-slate-500 mb-1 text-right">Summe (€)</label>}
                  <div className="w-full px-3 py-2 rounded-lg bg-slate-50 ring-1 ring-slate-200 text-right text-slate-600 font-mono text-sm">
                    {formatCurrency(Number(item.quantity) * Number(item.price))}
                  </div>
                </div>

                <button type="button" onClick={() => setFormData(f => ({ ...f, items: f.items.filter(i => i.id !== item.id) }))} className="p-2.5 text-slate-400 hover:text-red-600">
                  <SafeIcon icon={FiTrash} />
                </button>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t space-y-2">
            <div className="flex justify-end gap-12 text-slate-500 text-sm">
              <span>Zwischensumme (Netto):</span>
              <span className="w-32 text-right font-mono">{formatCurrency(nettoTotal)}</span>
            </div>
            <div className="flex justify-end gap-12 text-slate-500 text-sm">
              <span>zzgl. 19% MwSt:</span>
              <span className="w-32 text-right font-mono">{formatCurrency(vatAmount)}</span>
            </div>
            <div className="flex justify-end gap-12 pt-2">
              <span className="text-slate-900 font-bold">Gesamtbetrag (Brutto):</span>
              <span className="w-32 text-right text-xl font-bold text-blue-600 font-mono">{formatCurrency(bruttoTotal)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/')} className="px-6 py-2.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-colors">Abbrechen</button>
          <button type="submit" className="bg-blue-600 text-white px-8 py-2.5 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex items-center gap-2">
            <SafeIcon icon={FiSave} /> Gutschrift speichern
          </button>
        </div>
      </form>
    </div>
  );
}